import { BUILD_GIT_SHA, BUILD_ID, BUILD_TIME } from './buildInfo';

export const CHUNK_RELOAD_GUARD_KEY = 'aligned-beta.chunkReloadBuildId';
export const DATABASE_RELOAD_GUARD_KEY = 'aligned-beta.databaseReloadAt';
export const FIRESTORE_ASSERTION_RELOAD_GUARD_KEY = 'aligned-beta.firestoreAssertionReloadAt';

export interface DeployedVersion {
  buildId: string;
  builtAt: string;
  gitSha: string;
}

export interface PendingReloadInfo {
  fromBuildId: string;
  toBuildId: string;
  reason: string;
  detectedAt: string;
}

export const VERSION_URL = '/version.json';
export const PENDING_RELOAD_STORAGE_KEY = 'aligned-beta.pendingBuildReload';

export function currentBuildInfo(): DeployedVersion {
  return {
    buildId: BUILD_ID,
    builtAt: BUILD_TIME,
    gitSha: BUILD_GIT_SHA,
  };
}

export async function fetchDeployedVersion(
  fetchImpl: typeof fetch = fetch,
): Promise<DeployedVersion | null> {
  const response = await fetchImpl(`${VERSION_URL}?ts=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;
  const data = await response.json();
  if (!isDeployedVersion(data)) return null;
  return data;
}

export function shouldReloadForVersion(
  current: DeployedVersion,
  next: DeployedVersion | null,
): boolean {
  return !!next && next.buildId !== current.buildId;
}

export function isChunkLoadFailure(value: unknown): boolean {
  if (value == null) return false;

  if (typeof value === 'string') {
    // Every browser and Vite itself word a stale-chunk fetch failure
    // differently: Chrome says "Failed to fetch dynamically imported
    // module", Firefox says "error loading dynamically imported module",
    // Safari says "Importing a module script failed.", and Vite's own
    // CSS-preload guard says "Unable to preload CSS for <url>".
    //
    // The MIME wordings are the same failure seen through the hosting rewrite:
    // `**` → `/index.html` answers a request for a deleted chunk with the app
    // shell at 200 rather than a 404, so the browser rejects the HTML body
    // instead of the fetch. Safari phrases that as "'text/html' is not a valid
    // JavaScript MIME type.", Firefox as "was blocked because of a disallowed
    // MIME type", Chrome as "responded with a MIME type of".
    return /chunkloaderror|loading chunk [\w-]+ failed|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css for|is not a valid javascript mime type|blocked because of a disallowed mime type|responded with a mime type of/i
      .test(value);
  }

  if (value instanceof Error) {
    return isChunkLoadFailure(`${value.name} ${value.message}`);
  }

  if (typeof Event !== 'undefined' && value instanceof Event) {
    const target = value.target as { tagName?: string; src?: string; href?: string } | null;
    if (target) {
      const tag = (target.tagName || '').toUpperCase();
      // Vite emits hashed chunks under `/assets/`; `/static/` is the
      // Create React App layout this check was originally written against and
      // is kept so the same helper works if either layout is ever served.
      if ((tag === 'SCRIPT' || tag === 'LINK')
        && /\/(assets|static)\/.*\.(js|css|mjs)(\?|$)/i.test(target.src || target.href || '')) {
        return true;
      }
    }
    const eventLike = value as Event & { message?: unknown; error?: unknown };
    return isChunkLoadFailure(eventLike.error) || isChunkLoadFailure(eventLike.message);
  }

  if (typeof value === 'object') {
    const errorLike = value as { message?: unknown; name?: unknown; reason?: unknown };
    return isChunkLoadFailure(errorLike.reason)
      || isChunkLoadFailure(errorLike.message)
      || isChunkLoadFailure(errorLike.name);
  }

  return false;
}

export interface ChunkRecoveryDeps {
  storage: Storage | null;
  reload: () => void;
  buildId: string;
}

/**
 * `window.sessionStorage` is a getter that can itself throw (sandboxed
 * iframes without `allow-same-origin`, some privacy modes) rather than just
 * failing on `.getItem()`/`.setItem()`, so every caller must go through this
 * instead of reading `window.sessionStorage` directly.
 */
export function getSafeSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function resolveChunkRecoveryDeps(deps?: Partial<ChunkRecoveryDeps>): ChunkRecoveryDeps {
  // `'storage' in deps` rather than `??`, so an explicit null means "no storage"
  // instead of falling back to the real one.
  const storage = deps && 'storage' in deps ? (deps.storage ?? null) : getSafeSessionStorage();
  return {
    storage,
    reload: deps?.reload ?? (() => window.location.reload()),
    buildId: deps?.buildId ?? BUILD_ID,
  };
}

/**
 * Reload once per build id to pick up the current chunk names.
 *
 * Returns whether a reload was triggered. `false` means this build already
 * reloaded and still could not load the chunk, so the caller must surface the
 * error rather than loop: a genuinely missing chunk would otherwise reload the
 * tab forever. A successful recovery lands on a different build id, which
 * re-arms the guard on its own.
 *
 * The tradeoff is one missed auto-recovery: if a tab reloads for a *transient*
 * chunk failure and comes back on the same build, a later stale-build failure
 * in that same tab shows the reload prompt instead of reloading itself. That is
 * the safe direction to be wrong in, and the error boundary makes the next step
 * explicit.
 */
export function reloadForChunkFailure(deps?: Partial<ChunkRecoveryDeps>): boolean {
  const { storage, reload, buildId } = resolveChunkRecoveryDeps(deps);
  if (!storage) return false;
  try {
    if (storage.getItem(CHUNK_RELOAD_GUARD_KEY) === buildId) return false;
    storage.setItem(CHUNK_RELOAD_GUARD_KEY, buildId);
  } catch {
    // Private browsing can throw on read and write alike. With no storage there
    // is no loop protection, so decline to reload.
    return false;
  }
  reload();
  return true;
}

/**
 * Reload once per session after the browser drops the IndexedDB connection the
 * Firebase SDK depends on.
 *
 * The SDK reports this as "Connection to Indexed Database server lost. Refresh
 * the page to try again" — idle eviction, private-browsing storage limits,
 * multi-tab contention, or a sleep/wake cycle. Auth persistence is the consumer
 * here, and once the connection is gone the session state is stuck: the tab is
 * already broken, and the SDK's own message tells the user to do exactly what
 * this does for them.
 *
 * Guarded per session rather than per build, because unlike a stale chunk this
 * has nothing to do with which build is loaded — a second loss in the same tab
 * is a signal the reload did not help, so the user is left with the error
 * rather than a reload loop.
 */
export function reloadForLostDatabaseConnection(deps?: Partial<ChunkRecoveryDeps>): boolean {
  const { storage, reload } = resolveChunkRecoveryDeps(deps);
  if (!storage) return false;
  try {
    if (storage.getItem(DATABASE_RELOAD_GUARD_KEY)) return false;
    storage.setItem(DATABASE_RELOAD_GUARD_KEY, new Date().toISOString());
  } catch {
    // Private browsing can throw on read and write alike. With no storage there
    // is no loop protection, so decline to reload.
    return false;
  }
  reload();
  return true;
}

/**
 * The Firestore SDK's own wording when its internal state machine reaches a
 * state it does not expect and gives up — "FIRESTORE (12.18.0) INTERNAL
 * ASSERTION FAILED: Unexpected state (ID: b815)". In production this arrives
 * when a network blip interrupts an in-flight stream during an auth-token
 * refresh: the SDK throws instead of degrading, and the throw surfaces during
 * render rather than through an `onSnapshot` error callback.
 *
 * Matched on wording because that is all there is to key on: the error is a
 * plain `Error` with no code, and the assertion ids vary by call site. The
 * nested `CONTEXT` payload the SDK attaches can carry a second assertion
 * failure of its own, so the check also looks through a wrapped cause.
 */
export function isFirestoreInternalAssertion(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return /internal assertion failed/i.test(value);
  if (value instanceof Error) {
    return isFirestoreInternalAssertion(`${value.name} ${value.message}`)
      || isFirestoreInternalAssertion((value as Error & { cause?: unknown }).cause);
  }
  if (typeof value === 'object') {
    const errorLike = value as { message?: unknown; name?: unknown };
    return isFirestoreInternalAssertion(errorLike.message)
      || isFirestoreInternalAssertion(errorLike.name);
  }
  return false;
}

/**
 * Reload once per session after the Firestore SDK fails an internal assertion.
 *
 * Once the SDK's async queue has died there is no way back from inside the
 * page: every later read and write on that instance fails the same way, so the
 * tab is unusable until it is reloaded. This is the same bargain as
 * `reloadForLostDatabaseConnection` — do for the user the one thing that can
 * recover the tab — and it is guarded per session for the same reason: a second
 * assertion in the same tab means the reload did not help, so the user is left
 * with the error rather than a reload loop.
 */
export function reloadForFirestoreInternalAssertion(deps?: Partial<ChunkRecoveryDeps>): boolean {
  const { storage, reload } = resolveChunkRecoveryDeps(deps);
  if (!storage) return false;
  try {
    if (storage.getItem(FIRESTORE_ASSERTION_RELOAD_GUARD_KEY)) return false;
    storage.setItem(FIRESTORE_ASSERTION_RELOAD_GUARD_KEY, new Date().toISOString());
  } catch {
    // Private browsing can throw on read and write alike. With no storage there
    // is no loop protection, so decline to reload.
    return false;
  }
  reload();
  return true;
}

/**
 * Run a dynamic import with stale-build recovery: retry once, then reload.
 *
 * A deploy replaces every hashed chunk, so a tab still holding the previous
 * `index.html` fails with "Failed to fetch dynamically imported module" the
 * moment it navigates to a not-yet-loaded route. `AppUpdateProvider` recovers
 * from chunk failures it sees on `window`, but it never sees these: React.lazy
 * catches the import rejection itself and re-throws it during render, so no
 * global event is dispatched. Recovery has to wrap the import.
 *
 * The retry costs one request and covers the common cases — a chunk fetch that
 * lost the network, or one that raced a deploy mid-flight.
 */
export async function importWithChunkRecovery<T>(
  loader: () => Promise<T>,
  deps?: Partial<ChunkRecoveryDeps>,
): Promise<T> {
  try {
    return await loader();
  } catch (error) {
    if (!isChunkLoadFailure(error)) throw error;

    try {
      return await loader();
    } catch (retryError) {
      if (!isChunkLoadFailure(retryError)) throw retryError;
      if (reloadForChunkFailure(deps)) {
        // The reload is asynchronous. Block rather than letting Suspense
        // resolve with a broken component while the page tears down.
        await new Promise<never>(() => {});
      }
      throw retryError;
    }
  }
}

export function readPendingReloadInfo(storage: Storage | null): PendingReloadInfo | null {
  if (!storage) return null;
  const raw = storage.getItem(PENDING_RELOAD_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed
      && typeof parsed.fromBuildId === 'string'
      && typeof parsed.toBuildId === 'string'
      && typeof parsed.reason === 'string'
      && typeof parsed.detectedAt === 'string'
    ) {
      return parsed as PendingReloadInfo;
    }
  } catch {
    // Ignore malformed storage.
  }
  return null;
}

export function writePendingReloadInfo(storage: Storage | null, info: PendingReloadInfo): void {
  try {
    storage?.setItem(PENDING_RELOAD_STORAGE_KEY, JSON.stringify(info));
  } catch {
    // QuotaExceededError or SecurityError in private browsing is non-critical.
  }
}

export function clearPendingReloadInfo(storage: Storage | null): void {
  storage?.removeItem(PENDING_RELOAD_STORAGE_KEY);
}

function isDeployedVersion(value: unknown): value is DeployedVersion {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  return typeof data.buildId === 'string'
    && typeof data.builtAt === 'string'
    && typeof data.gitSha === 'string';
}
